import {unwrapResult} from '@reduxjs/toolkit';
import {Card, Form as AntForm} from 'antd';
import {useForm} from 'antd/es/form/Form';
import React, {
    FC,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import {dispatchFormWatcherEvent} from 'components/form/hooks/use-form-watcher';
import {SignAndFileListUploader} from 'components/form/inputs/uploader-list/sign-and-file-list-uploader/sign-and-file-list-uploader';
import {REQUESTS_DISPLAY_FIELDS_URL} from 'components/request/constants/requests.constants';
import {RequestCardContext} from 'components/request/replies/context/request-card-context';
import {Replies} from 'components/request/replies/replies';
import {RequestFormFields} from 'components/request/request-form/components/request-from-fields/request-form-fields';
import {RequestResponsible} from 'components/request/request-form/components/request-responsible/request-responsible';
import {
    REQUEST_FIELD_CONTENT,
    requestMainFields,
    ERequestStatus,
    ERequestTypeCode,
} from 'components/request/request-form/request-form.constants';
import {StatusModelAwareActions} from 'components/request/status-model-aware-actions/status-model-aware-actions';
import {convertRequestData} from 'components/request/utils/request-api-utils';
import {RichTextEditor} from 'components/rich-text';
import {normalizeFormDataForRequest} from 'modules/data/utils/data.utils';
import {ECommonMessages, EErrorMessages} from 'shared/constants/messages';
import {
    URL_REQUESTS, URL_REQUESTS_REQUIREMENT_PROLONGATION_UPDATE,
} from 'shared/constants/urls';
import {ELocationStateKey, useAppHistory} from 'shared/hooks/use-app-history';
import {IFileDto} from 'shared/types/files';
import {showMessage} from 'shared/utils';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {
    selectRequestData,
    selectRequestFields,
    selectRequestReplies,
} from 'store/slices/request-slice/request-selectors';
import {requestSliceActions} from 'store/slices/request-slice/request-slice';
import {
    loadRequestData,
    loadRequestFieldsMetadata, loadRequestStatusesData,
    saveRequestData,
} from 'store/slices/request-slice/request-slice-thunks';
import {IRequestInfo, ITopicReportInfo} from 'store/slices/request-slice/request-slice-type';
import {normalizeBoolValue} from 'utils/common';

import s from './request-information.module.less';

interface IRequestInformationProps {
    entityName: string;
    requestFormKey: string;
    requestMetaName: string;
    data?: IRequestInfo;
    requestType?: string;
    onClose?: () => void;
    enableLinkToReport?: boolean;
    topicReportInfo?: ITopicReportInfo;
    hasPermissionDisplayFileComponent: boolean;
}

export const RequestInformation: FC<IRequestInformationProps> = memo(({
    entityName,
    data,
    onClose,
    requestMetaName,
    requestFormKey,
    requestType,
    enableLinkToReport = false,
    topicReportInfo,
    hasPermissionDisplayFileComponent,
}:IRequestInformationProps) => {
    const history = useAppHistory();
    const [form] = useForm();
    const dispatch = useAppDispatch();
    const {
        id: requestId,
        content,
        topicRequestType,
        requestType: dataRequestType,
        topicStatus,
        idRequestComment: requestCommentId,
        attachmentList,
    } = data ?? {};
    const [messageCollapseOpen, setMessageCollapseOpen] = useState(false);
    const [isEditingForm, setEditingForm] = useState(false);
    const [fileList, setFileList] = useState<IFileDto[] | undefined>(attachmentList);

    const requestData = useAppSelector(selectRequestData);
    const fields = useAppSelector(selectRequestFields);
    const {resetRequestStatusesData} = requestSliceActions;
    const requestReplies = useAppSelector(selectRequestReplies);

    const hasReplies = requestReplies && !!requestReplies?.length;
    const typeCode = topicRequestType?.typeCode || dataRequestType;
    const isHidden = topicStatus?.lookupCode === ERequestStatus.IN_PREPARATION_RESPONSE;
    const isResponsibleVisible = topicStatus?.lookupCode === ERequestStatus.DRAFT
        || typeCode === ERequestTypeCode.REQUEST || typeCode === ERequestTypeCode.REQUEST_PUD;
    const topicRequestId = topicRequestType?.id;
    const formData = useMemo(() => convertRequestData(requestData, data), [requestData, data]);
    const hasDisplayFileListUploader = hasPermissionDisplayFileComponent
        && (!!attachmentList?.length || isEditingForm);

    const reportLink = topicReportInfo?.docId === '-1'
        ? `/table-reports/${topicReportInfo.templateCode}`
        : `/table-reports/${topicReportInfo?.docId}/${topicReportInfo?.templateCode}`;

    const initialValues = useMemo(() => ({
        ...data,
        ...formData,
    }), [data, formData]);

    useEffect(() => {
        setFileList(attachmentList);
    }, [attachmentList]);

    useEffect(() => {
        if (!fields && topicRequestId) {
            dispatch(loadRequestFieldsMetadata({
                entityName: requestMetaName,
                referenceUrl: REQUESTS_DISPLAY_FIELDS_URL,
                id: topicRequestId,
            }));
        }
    }, [fields, topicRequestId, requestMetaName]);

    const handleFinish = useCallback((values: any) => {
        if (requestCommentId && requestId) {
            form.validateFields().then(() => {
                dispatch(saveRequestData({
                    commentId: requestCommentId,
                    url: URL_REQUESTS_REQUIREMENT_PROLONGATION_UPDATE,
                    data: normalizeFormDataForRequest(values, [...fields || [], ...requestMainFields]),
                })).then(unwrapResult)
                    .then(() => {
                        form.resetFields();
                        setEditingForm(!isEditingForm);
                        showMessage({message: ECommonMessages.SAVE_SUCCESS});
                        dispatch(resetRequestStatusesData);
                        dispatch(loadRequestStatusesData({commentId: requestCommentId, requestFormKey}));
                        dispatch(loadRequestData({id: requestId, url: URL_REQUESTS}));
                    }, error => {
                        showMessage({
                            message: error?.response?.data ?? EErrorMessages.UNEXPECTED_ERROR_OCCURRED,
                            isError: true,
                        });
                    });
            });
        }
    }, [requestCommentId, requestId, form, isEditingForm, requestFormKey, fields]);

    const contextValue = useMemo(() => ({
        text: messageCollapseOpen
            ? 'Свернуть всю переписку'
            : 'Развернуть всю переписку',
        isEditing: isEditingForm || false,
        handleCollapse: () => setMessageCollapseOpen(v => !v),
        setEditing: () => setEditingForm(v => !v),
    }), [messageCollapseOpen, isEditingForm]);

    return (
        <>
            <div className={s['request-information__actions']}>
                <RequestCardContext.Provider value={contextValue}>
                    <StatusModelAwareActions
                        requestFormKey={requestFormKey}
                        removeControlCollapseAction={hasReplies}
                        requestCommentId={requestCommentId}
                        onClose={onClose}
                        entityName={entityName}
                        isHidden={isHidden}
                        requestType={requestType}
                        onSubmit={() => {
                            form.submit();
                        }}
                        requestInfo={requestData?.requests}
                    />
                </RequestCardContext.Provider>
            </div>
            <AntForm
                layout="vertical"
                form={form}
                className="form"
                initialValues={initialValues}
                onFinish={handleFinish}
                onValuesChange={(_, values: any) => {
                    dispatchFormWatcherEvent({values, targetForm: form});
                }}
            >
                {fields && (
                    <>
                        {isResponsibleVisible && (
                            <RequestResponsible
                                isVisible={normalizeBoolValue(isResponsibleVisible)}
                            />
                        )}
                        <RequestFormFields
                            entityName={entityName}
                            data={formData}
                            fields={[...(fields || [])]}
                            form={form}
                            isEditable={isEditingForm}
                        />
                        {content && (
                            <AntForm.Item
                                name="content"
                                label={REQUEST_FIELD_CONTENT.label}
                            >
                                <RichTextEditor
                                    key="content"
                                    disabled={!isEditingForm}
                                    value={content}
                                />
                            </AntForm.Item>
                        )}
                        <AntForm.Item
                            name="files"
                        >
                            {hasDisplayFileListUploader && (
                                <SignAndFileListUploader
                                    disabled={!isEditingForm}
                                    fileList={fileList}
                                    onChange={setFileList}
                                    requestCommentId={requestCommentId}
                                    dataKeyForDownload="attachmentId"
                                    url={entityName}
                                    signUrl={entityName}
                                    isDownloadDocumentList={!!attachmentList?.length}
                                />
                            )}
                        </AntForm.Item>
                    </>
                )}
            </AntForm>
            {
                enableLinkToReport && (
                    <section>
                        <h4 className={s.section__title}>
                            Предмет запроса
                        </h4>
                        <button
                            type="button"
                            role="link"
                            className={s.section__link}
                            onClick={() => {
                                if (topicReportInfo?.reportParameters === null && topicReportInfo?.docId !== '-1') {
                                    history.pushWithStateUpdate(reportLink, {
                                        [ELocationStateKey.TABLE_REPORT_REQUEST_GENERAL_PARAMS]: {
                                            TOPIC_REQUEST_ID: requestId,
                                        },
                                    });
                                } else {
                                    const reportParameters = JSON.parse(topicReportInfo?.reportParameters || '');
                                    history.pushWithStateUpdate(reportLink, {
                                        [ELocationStateKey.TABLE_REPORT_REQUEST_GENERAL_PARAMS]: {
                                            TOPIC_REQUEST_ID: requestId,
                                            ...reportParameters,
                                        },
                                    });
                                }
                            }}
                        >
                            Просмотреть строки отчета, по которым сформирован запрос
                        </button>
                    </section>
                )
            }
            {fields && hasReplies && (
                <Card
                    className={s['replies-card']}
                >
                    <Replies
                        entityName={entityName}
                        data={requestReplies}
                        collapseIsOpen={messageCollapseOpen}
                    />
                </Card>
            )}
        </>
    );
});
